import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BuilderPage, TableAdapter, VariableSelectFeatureFlag } from '@makyoapp/makyo-common'
import { ColorVariant, MaterialIcon, TopAppBar } from '@makyoapp/makyo-tw-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useBuilderActionContextV2 } from '@/context/__project__/BuilderActionContextV2'
import { useCheckVisibilityV2 } from '@/hooks/__project__/useCheckVisibilityV2'
import { useActiveDevice } from '@/hooks/useActiveDevice'
import { THUMB_AVATAR } from '@/shared/constants'
import { parseLabel } from '@/shared/utils'

import { useGetBadgeContent } from './hooks/useGetBadgeContent'

interface BuilderTopbarCheckedMode extends React.ComponentProps<'div'> {
  color: ColorVariant
  page: BuilderPage
}

export function TopBarCheckedMode({ page, color }: BuilderTopbarCheckedMode) {
  const { t } = useTranslation()

  const { simulateAction } = useBuilderActionContextV2()
  const [viewport] = useActiveDevice()

  const { listChecked, setListChecked } = useAppBuilderContext()
  const { currentUser } = useAppAuthContext()

  const { menus } = useAppBuilderContext()
  const menu = menus.find(({ id }) => id === listChecked?.action?.settings?.value)

  const { activePageTableId, activePageRowIndex, activeLayerDetailRowId } = useActiveLayerContext()

  const { getBadgeContent } = useGetBadgeContent()

  const maxVisibleButtons = viewport === 'phone' ? 3 : 4

  const { checkVisibility } = useCheckVisibilityV2()
  const shouldShow = checkVisibility({ visibility: menu?.visibility })

  const trailing = useMemo(() => {
    const isActionButtonsHaveIconOrImage = menu?.items.some((m) => {
      const isIcon = m?.icon?.flag === VariableSelectFeatureFlag.Icon
      const isAvatar = m?.icon?.flag === VariableSelectFeatureFlag.Avatar
      const isImage = m?.icon?.flag === VariableSelectFeatureFlag.Image
      return isIcon || isAvatar || isImage
    })

    const actionButtons = menu?.items.map((m) => {
      const isIcon = m?.icon?.flag === VariableSelectFeatureFlag.Icon
      const isAvatar = m?.icon?.flag === VariableSelectFeatureFlag.Avatar
      const isImage = m?.icon?.flag === VariableSelectFeatureFlag.Image

      let media: { icon?: React.ReactNode | undefined; image?: string | undefined } = {
        icon: isActionButtonsHaveIconOrImage ? <div className='w-6 h-6 visible-hidden' /> : undefined,
        image: undefined,
      }

      if (isIcon) {
        media = { icon: <MaterialIcon icon={(m.icon as any)?.value} size={24} /> }
      } else if (isAvatar) {
        media = { image: currentUser?.photo || THUMB_AVATAR, icon: undefined }
      } else if (isImage) {
        media = { image: (m.icon as any)?.value, icon: undefined }
      }

      const badge = getBadgeContent(m)

      let theBadge = undefined

      if (badge.showBadge) {
        if (badge.badgeSize === 's') {
          theBadge = null
        } else {
          theBadge = badge.badgeValue
        }
      }

      const handleClick = () => {
        const checkedListTableId = listChecked?.extraActionProps.tableId as string | undefined
        const isMultiple = !!listChecked && !!listChecked.items.length && !!checkedListTableId

        if (m.actions && m.actions?.length > 0) {
          simulateAction({
            actions: m.actions,
            rowIndex: activePageRowIndex,
            tableId: activePageTableId,
            rowId: activeLayerDetailRowId,
            multipleOptions: isMultiple
              ? {
                  tableId: checkedListTableId,
                  rowIds: listChecked.items,
                }
              : undefined,
          })
        }
      }

      return {
        ...media,
        label: parseLabel(m.title),
        badgeText: theBadge,
        onClick: handleClick,
      }
    })

    return actionButtons
  }, [
    activePageRowIndex,
    activePageTableId,
    activeLayerDetailRowId,
    currentUser?.photo,
    getBadgeContent,
    listChecked,
    simulateAction,
    menu?.items,
  ])

  if (!shouldShow) return null

  return (
    <TopAppBar
      title={t('builderListItemCount', {
        count: listChecked?.items.length,
      })}
      leadingIcon={<MaterialIcon icon='close' size={24} />}
      onMenuClick={() => {
        setListChecked(undefined)
      }}
      maxVisibleButtons={maxVisibleButtons}
      actionButtons={trailing}
      backgroundColor={color}
      className='sticky! top-0 left-0 z-50'
    />
  )
}
